import type { api } from "@breakout/backend/convex/_generated/api"
import { formatUsdc } from "@breakout/sdk"
import { TRADING } from "@breakout/shared/constants"
import { toast } from "@breakout/ui/components/Toast"
import { cn, formatShares, formatUsd } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import type { FunctionReturnType } from "convex/server"
import { Check, ExternalLink, Loader2, X } from "lucide-react"
import { useState } from "react"
import type { Address } from "viem"
import { type MarketState, useMarketResolution } from "../../hooks/market/useMarketResolution"
import { useClaim } from "../../hooks/trading/useClaim"
import { getOutcomeDirection } from "../../lib/constants"
import { useBreakoutConfig } from "../../lib/providers"
import { parseTradeErrorMessage } from "../../lib/sentry"
import { SimpleAvatar } from "../ui/SimpleAvatar"

type EnhancedPosition = FunctionReturnType<
	typeof api.indexer.public.getUserPositionsEnhanced
>["positions"][number]

type EnhancedPositionCardProps = {
	position: EnhancedPosition
	userAddress: Address
} & (
	| { variant: "owner" } // Own profile - can claim
	| { variant: "public" } // Public profile - view only
)

export function EnhancedPositionCard(props: EnhancedPositionCardProps) {
	const { position, userAddress, variant } = props
	const canInteract = variant === "owner"
	const config = useBreakoutConfig()
	const claimMutation = useClaim()
	const [claimedAmount, setClaimedAmount] = useState<number | null>(null)

	const {
		marketState: hookMarketState,
		claimableAmount,
		winningBucket,
	} = useMarketResolution({
		marketAddress: position.marketAddress as Address,
		userAddress,
		cutoffTimeSec: position.cutoffTimeSec,
	})

	const marketState: MarketState = claimedAmount !== null ? "claimed" : hookMarketState

	const handleClaim = async () => {
		try {
			const result = await claimMutation.mutateAsync({
				marketAddress: position.marketAddress as Address,
			})
			setClaimedAmount(result.amountClaimed)

			const explorerUrl = `${config.chainConfig.explorer}/tx/${result.transactionHash}`
			toast.success(
				<div className="flex items-center gap-2">
					<span>Claimed {formatUsdc(result.amountClaimed)} USDC!</span>
					<a
						aria-label="View transaction on block explorer (opens in new tab)"
						className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
						href={explorerUrl}
						onClick={e => e.stopPropagation()}
						rel="noopener noreferrer"
						target="_blank"
					>
						View
						<ExternalLink aria-hidden="true" className="size-3" />
					</a>
				</div>,
			)
		} catch (err) {
			const message = parseTradeErrorMessage({ error: err, action: "claim" })
			toast.error(message)
		}
	}

	const isBinary = position.outcomeLabels.length <= 2
	const direction = getOutcomeDirection(position.bucketIndex, isBinary)

	const isWinner = winningBucket === position.bucketIndex
	const isClaimInProgress = claimMutation.status === "processing"
	const isClaimDisabled = isClaimInProgress || claimMutation.isPending
	const isClaimBelowMinimum = claimableAmount > 0n && claimableAmount < TRADING.MIN_BUY_AMOUNT_RAW
	const canClaim =
		marketState === "finalized" && isWinner && claimableAmount > 0n && !isClaimBelowMinimum
	const isActive = !position.isResolved && !position.isFinalized
	const isPending = !isActive && !position.isFinalized

	// Compute display values
	const avgPrice = position.avgPriceCents !== null ? `${Math.round(position.avgPriceCents)}¢` : "—"
	const currentPrice =
		position.currentPriceCents !== null ? `${Math.round(position.currentPriceCents)}¢` : "—"

	// For active/pending: current value based on market price and P&L
	// Backend calculates resolved positions using payout, but for pending we want market-based value
	let activeValue: number
	let activePnl: number
	let activePnlPercent: number | null

	if (isPending && position.currentPriceCents !== null) {
		// For pending, recalculate using market price (same as active formula)
		activeValue = position.shares * (position.currentPriceCents / 100)
		activePnl = activeValue - position.costBasis
		activePnlPercent = position.costBasis > 0 ? (activePnl / position.costBasis) * 100 : null
	} else {
		// For active, use backend-computed values
		activeValue = position.currentValue
		activePnl = position.pnlUsdc
		activePnlPercent = position.pnlPercent
	}

	// For closed: cost basis (total bet) and payout (amount won)
	const totalBet = position.costBasis
	const amountWon = isWinner ? position.shares : 0
	const closedProfit = amountWon - totalBet
	const closedProfitPercent = totalBet > 0 ? (closedProfit / totalBet) * 100 : 0

	// P&L color helper
	const getPnlColor = (pnl: number): string => {
		if (pnl > 0) {
			return "text-emerald-600"
		}
		if (pnl < 0) {
			return "text-rose-600"
		}
		return "text-text-secondary"
	}

	// avatarUrl is always provided by the backend (with FFG/unavatar fallbacks applied server-side)

	// Render different layouts for active/pending vs closed
	if (isActive || isPending) {
		return (
			<Link
				className="block rounded-xl border border-border-subtle bg-bg-card p-3 transition-colors hover:bg-bg-secondary/40 sm:p-4"
				data-testid="enhanced-position-card"
				params={{ marketId: position.marketAddress }}
				to="/markets/$marketId"
			>
				<div className="flex items-center gap-3">
					{/* Avatar */}
					<SimpleAvatar
						alt={position.displayName}
						className="shrink-0"
						size="sm"
						src={position.avatarUrl}
						variant="square"
					/>

					{/* Market Name + Info */}
					<div className="min-w-0 flex-1">
						<p className="truncate font-medium text-sm text-text-primary">{position.displayName}</p>
						<p className="mt-0.5 truncate text-text-secondary text-xs">
							<span
								className={cn(
									"font-semibold",
									direction === "up" ? "text-emerald-600" : "text-rose-600",
								)}
							>
								{position.displayLabel}
							</span>
							<span className="tabular-nums">
								{" "}
								{formatShares(position.shares)} shares at {avgPrice}
							</span>
						</p>
					</div>

					{/* Desktop: Avg, Current, Value columns */}
					<div className="hidden items-center gap-3 sm:flex">
						{/* Avg */}
						<div className="w-16 text-right">
							<p className="text-sm text-text-primary tabular-nums">{avgPrice}</p>
						</div>

						{/* Current */}
						<div className="w-16 text-right">
							<p className="text-sm text-text-primary tabular-nums">{currentPrice}</p>
						</div>

						{/* Value + P&L */}
						<div className="w-28 text-right">
							<p className="font-semibold text-sm text-text-primary tabular-nums">
								{formatUsd(activeValue)}
							</p>
							<p className={`text-xs tabular-nums ${getPnlColor(activePnl)}`}>
								{activePnl >= 0 ? "" : "-"}${Math.abs(activePnl).toFixed(2)}
								{activePnlPercent !== null && (
									<span className="ml-0.5">
										({activePnlPercent >= 0 ? "" : ""}
										{activePnlPercent.toFixed(2)}%)
									</span>
								)}
							</p>
						</div>
					</div>

					{/* Mobile: Value + P&L only */}
					<div className="text-right sm:hidden">
						<p className="font-semibold text-sm text-text-primary tabular-nums">
							{formatUsd(activeValue)}
						</p>
						<p className={`text-xs tabular-nums ${getPnlColor(activePnl)}`}>
							{activePnl >= 0 ? "+" : ""}
							{formatUsd(activePnl)}
						</p>
					</div>

					{/* Action spacer for alignment */}
					<div className="hidden w-10 shrink-0 sm:block" />
				</div>
			</Link>
		)
	}

	// Closed position layout
	return (
		<Link
			className="block rounded-xl border border-border-subtle bg-bg-card p-3 transition-colors hover:bg-bg-secondary/40 sm:p-4"
			data-testid="enhanced-position-card"
			params={{ marketId: position.marketAddress }}
			to="/markets/$marketId"
		>
			<div className="flex items-center gap-3">
				{/* Result column - desktop */}
				<div className="hidden w-16 shrink-0 items-center gap-1.5 sm:flex">
					{isWinner ? (
						<>
							<div className="flex size-5 items-center justify-center rounded-full bg-emerald-500">
								<Check aria-hidden="true" className="size-3 text-white" strokeWidth={3} />
							</div>
							<span className="font-medium text-emerald-600 text-sm">Won</span>
						</>
					) : (
						<>
							<div className="flex size-5 items-center justify-center rounded-full bg-rose-500">
								<X aria-hidden="true" className="size-3 text-white" strokeWidth={3} />
							</div>
							<span className="font-medium text-rose-600 text-sm">Lost</span>
						</>
					)}
				</div>

				{/* Avatar */}
				<SimpleAvatar
					alt={position.displayName}
					className="shrink-0"
					size="sm"
					src={position.avatarUrl}
					variant="square"
				/>

				{/* Market Name + Info */}
				<div className="min-w-0 flex-1">
					<p className="truncate font-medium text-sm text-text-primary">{position.displayName}</p>
					<p className="mt-0.5 truncate text-text-secondary text-xs">
						<span className="tabular-nums">{formatShares(position.shares)} </span>
						<span
							className={cn(
								"font-semibold",
								direction === "up" ? "text-emerald-600" : "text-rose-600",
							)}
						>
							{position.displayLabel}
						</span>
						<span className="tabular-nums"> at {avgPrice}</span>
					</p>
				</div>

				{/* Desktop: Value + P&L */}
				<div className="hidden w-28 text-right sm:block">
					<p className="font-semibold text-sm text-text-primary tabular-nums">
						{formatUsd(amountWon)}
					</p>
					<p className={`text-xs tabular-nums ${getPnlColor(closedProfit)}`}>
						{closedProfit >= 0 ? "" : "-"}${Math.abs(closedProfit).toFixed(2)} (
						{closedProfitPercent >= 0 ? "" : ""}
						{closedProfitPercent.toFixed(2)}%)
					</p>
				</div>

				{/* Mobile: Value + P&L */}
				<div className="text-right sm:hidden">
					<p className="font-semibold text-sm text-text-primary tabular-nums">
						{formatUsd(amountWon)}
					</p>
					<p className={`text-xs tabular-nums ${getPnlColor(closedProfit)}`}>
						{closedProfit >= 0 ? "+" : ""}
						{formatUsd(closedProfit)}
					</p>
				</div>

				{/* Action column */}
				<div className="hidden w-10 shrink-0 items-center justify-end sm:flex">
					{canClaim && canInteract && (
						<button
							className="rounded-lg bg-emerald-600 px-3 py-1.5 font-medium text-white text-xs transition-colors hover:bg-emerald-700 disabled:opacity-50"
							disabled={isClaimDisabled}
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								void handleClaim()
							}}
							type="button"
						>
							{isClaimInProgress ? (
								<Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
							) : (
								"Claim"
							)}
						</button>
					)}
				</div>
			</div>
		</Link>
	)
}
